import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  Tooltip,
  Typography
} from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  IAttributeOptions,
  ILooseObject,
  INPUT_FIELD_TYPE_MAP,
  INPUT_TYPES_ENUM,
  UI_TYPE_INPUT_TYPE_MAP
} from '@/src/types/common.interface';
import lightTheme from '@/src/styles/themes/lightTheme';
import UseNavigation from '@/src/hooks/useNavigation';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import DynamicInput from '@/src/components/common/DynamicInput';
import { store } from '@/src/datastore/store';

type roleNodeDetailsProps = {
  roleDetails: ILooseObject;
};

const RoleNodeDetails = (props: roleNodeDetailsProps) => {
  const { roleDetails } = props;

  const [roleUiMode] = useState<'VIEW' | 'EDIT'>('VIEW');
  const [inputOptions, setInputOptions] = useState<ILooseObject>({});
  const [defaultRoleValues, setDefaultRolesValues] = useState<ILooseObject>({});
  const [isOptionsLoading] = useState<ILooseObject>({});

  const navigation = UseNavigation();
  const userAppPermissions: ILooseObject =
    store.getState()?.userDetails?.OptionsAndPermissions?.appPermissions;

  const {
    control: controlRoleDetails,
    formState: { errors: errorsRoleDetails },
    clearErrors: clearErrorsRoleDetails,
    reset: resetRoleDetails
  } = useForm({
    defaultValues: defaultRoleValues,
    mode: 'onBlur'
  });

  const processDefaultRoleData = (data: ILooseObject) => {
    const application = data?.application?.ui_label ?? '-';
    const permissions =
      data?.permissions?.map((permission: ILooseObject) => permission?.permission_key) ?? [];
    const role = data?.ui_label ?? '-';
    return { application, permissions, role };
  };

  const getAppPermissions = (applicationKey?: string) => {
    if (applicationKey) {
      return userAppPermissions?.[applicationKey];
    }
    // If no applicationKey is provided, return all permissions
    return Object.values(userAppPermissions)?.reduce((acc: string[], permissions: string[]) => {
      return Array.from(new Set([...acc, ...permissions]));
    }, []);
  };

  useEffect(() => {
    const permissionsOptions: IAttributeOptions[] = roleDetails?.role?.permissions?.map(
      (permission: ILooseObject) => ({
        label: permission?.ui_label ?? '-',
        value: permission?.permission_key ?? '-',
        details: permission?.description
      })
    );
    const processedRoleData = processDefaultRoleData(roleDetails?.role);
    setInputOptions((prev: ILooseObject) => ({
      ...prev,
      permissions: permissionsOptions
    }));
    setDefaultRolesValues(processedRoleData);
    resetRoleDetails(processedRoleData);
  }, [resetRoleDetails, roleDetails]);

  return (
    <Box component="form" display="flex" flexDirection="column" rowGap={2} m={2}>
      <Box
        border={1}
        borderColor={lightTheme.palette.grey[300]}
        borderRadius={3}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
      >
        <Typography
          variant="body1"
          fontWeight="bold"
          textTransform="none"
          color="primary.dark"
          pb={2}
          textAlign="center"
        >
          Role details
        </Typography>
        <Grid2 container columnSpacing={10}>
          {AttributesConfig.ROLE_DETAILS.map((attribute: IAttributeDef) => {
            const inputType = UI_TYPE_INPUT_TYPE_MAP[attribute?.ui_type?.toUpperCase()];
            const options = attribute?.options ?? inputOptions?.[attribute?.name] ?? [];
            const inputFieldType: INPUT_TYPES_ENUM =
              INPUT_FIELD_TYPE_MAP[attribute?.value_type?.toUpperCase()];
            const isDisabled = !attribute?.is_editable || roleUiMode === 'VIEW';
            const isVisible: boolean = !!attribute?.is_visible;
            if (!isVisible) {
              return null;
            }

            return (
              <Grid2
                container
                size={12}
                key={attribute?.attribute_id}
                alignItems="center"
                display="block"
              >
                {/* UI label */}
                <Box display="flex" pb={1}>
                  <Typography>{`${attribute?.ui_label} ${attribute?.is_mandatory ? '*' : ''}`}</Typography>
                </Box>
                {/* DynamicInput */}
                {isOptionsLoading?.[attribute?.name] ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <Controller
                    name={attribute.name as string}
                    control={controlRoleDetails}
                    render={({ field: { onChange, onBlur, value, name } }) => (
                      <Box>
                        <DynamicInput
                          type={inputType}
                          id={attribute?.attribute_id}
                          label={attribute?.ui_label}
                          value={value}
                          required={attribute?.is_mandatory}
                          hasError={!!(errorsRoleDetails?.[name]?.message as string)}
                          helperText={(errorsRoleDetails?.[name]?.message as string) ?? ' '}
                          options={options}
                          maxLength={attribute?.max_length}
                          disabled={isDisabled}
                          placeholder={attribute?.placeholder}
                          disableClearable={attribute?.disable_clearable}
                          onChange={(e: React.FormEvent<HTMLInputElement>) => {
                            onChange(e);
                            clearErrorsRoleDetails(name);
                          }}
                          onBlur={onBlur}
                          inputType={inputFieldType}
                        />
                      </Box>
                    )}
                  />
                )}
              </Grid2>
            );
          })}
        </Grid2>
      </Box>
      <Box display="flex" gap={1}>
        {(getAppPermissions(roleDetails?.role?.application_key)?.includes('permission_view') ||
          getAppPermissions(roleDetails?.role?.application_key)?.includes('role_users_view')) && (
          <Button
            variant="text"
            endIcon={<KeyboardArrowRightIcon />}
            size="small"
            sx={{ textTransform: 'none' }}
            onClick={() =>
              navigation.navigateTo(
                `/roles/role-details?roleKey=${roleDetails?.role?.role_key}&applicationKey=${roleDetails?.role?.application_key}`
              )
            }
          >
            View users
          </Button>
        )}
      </Box>
      {roleDetails?.childRoles?.length > 0 && (
        <>
          <Divider />
          <Box
            border={1}
            borderColor={lightTheme.palette.grey[300]}
            borderRadius={3}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              rowGap: 1
            }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              textTransform="none"
              color="primary.dark"
              pb={2}
              textAlign="center"
            >
              Derived roles and permissions
            </Typography>
            {roleDetails?.childRoles?.length > 0 &&
              roleDetails?.childRoles?.map((childRole: ILooseObject) => {
                return (
                  <Box key={childRole?.role_key}>
                    <Divider />
                    <Typography fontWeight="bold">{childRole?.ui_label}</Typography>
                    <Box
                      display="flex"
                      gap={1}
                      sx={{
                        mr: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center'
                      }}
                      flexBasis="auto"
                    >
                      {childRole?.permissions?.map((permission: ILooseObject) => (
                        <Box key={permission?.name} component="span">
                          <Tooltip title={permission?.description ?? ''} placement="top" arrow>
                            <Chip label={permission?.ui_label} variant="filled" sx={{ mb: 1 }} />
                          </Tooltip>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
          </Box>
        </>
      )}
    </Box>
  );
};

export default RoleNodeDetails;
